import { type SyntheticEvent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ComboboxItem, type ComboboxItemOptions } from 'ariakit'
import { Button, csx, FlexSpacer, Stack } from '@vtex/admin-ui'
import { type ProductSearchByOptions } from '@/components/products-listing/components/ProductSearch/hooks'
import { Tooltip } from '@/components/Tooltip'

import PinIcon from '../PinIcon'

import * as styles from './ComboboxSearchItem.styles'

type ComboboxSearchItemProps = ComboboxItemOptions & {
  isSelected: boolean
  isPinned: boolean
  id: ProductSearchByOptions
  onClick: (evt?: SyntheticEvent<HTMLButtonElement>) => void
  onTogglePin: (evt: SyntheticEvent<HTMLButtonElement>) => void
  value: string
  focusable?: boolean
}

export const ComboboxSearchItem = ({
  isSelected,
  id,
  onClick,
  value,
  isPinned = false,
  onTogglePin,
  focusable = true,
  ...props
}: ComboboxSearchItemProps) => {
  const { formatMessage } = useIntl()

  const tooltipText = formatMessage({
    id: isPinned
      ? 'admin/admin.catalog.products-search.remove-pin'
      : 'admin/admin.catalog.products-search.pin-term',
    defaultMessage: isPinned ? 'Unpin' : 'Pin term',
  })

  return (
    <ComboboxItem
      as="button"
      className={styles.comboboxItem}
      value={value}
      onClick={onClick}
      autoFocus={isSelected}
      aria-selected={isSelected}
      focusable={focusable}
      {...props}
    >
      <Stack
        direction="row"
        className={csx(
          styles.selectedItemVariant({
            selected: isSelected,
          })
        )}
      >
        <span className={styles.textSearch}>
          <FormattedMessage
            id={`admin/admin.catalog.products-search-${id}`}
            defaultMessage={getDefaultSearchMessage(id)}
            values={{
              b: (str) => (
                <strong className={csx(styles.searchingBy)}>{str}</strong>
              ),
            }}
          />
        </span>
        <span className={styles.value}>{value}</span>
        <FlexSpacer />
        <Tooltip text={tooltipText}>
          <Button
            className={`${styles.pinButton} ${
              isPinned ? 'show' : 'show-on-hover'
            }`}
            aria-label={tooltipText}
            variant="neutralTertiary"
            onClick={onTogglePin}
          >
            <PinIcon
              className="fullfield-pin"
              width="10px"
              height="16px"
              variant={isPinned ? 'fullfield' : 'default'}
            />
            {isPinned && <PinIcon className="dashed-pin" variant="unpin" />}
          </Button>
        </Tooltip>
      </Stack>
    </ComboboxItem>
  )
}

function getDefaultSearchMessage(id: ProductSearchByOptions) {
  switch (id) {
    case 'ean':
      return '<b>SKU EAN</b> starts with'
    case 'productName':
      return '<b>Product name</b> contains'
    case 'skuName':
      return '<b>SKU name</b> starts with'
    case 'productRefId':
      return '<b>Product reference code</b> starts with'
    case 'skuRefId':
      return '<b>SKU reference code</b> starts with'
    case 'productId':
      return '<b>Product ID</b> is'
    case 'skuId':
      return '<b>SKU ID</b> is'
    default:
      return ''
  }
}
